// src/pages/Acess/Login.tsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate, Link, useNavigate } from "react-router-dom";
import { ReactComponent as EyeOpen } from "../../assets/icons/eye.svg";
import { ReactComponent as EyeClosed } from "../../assets/icons/eye-slash.svg";
import { useTheme } from "../../context/ThemeContext";
import { useAuth } from "../../context/auth-context";

/* ============================================================
 * Helper para pegar lat/lon via navegador
 * ============================================================ */
const getBrowserLocation = (): Promise<{
  latitude: number;
  longitude: number;
}> => {
  return new Promise((resolve, reject) => {
    if (!("geolocation" in navigator)) {
      return reject(new Error("Geolocalização não suportada"));
    }

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        resolve({
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
        });
      },
      (err) => reject(err),
      { enableHighAccuracy: true, timeout: 5000 }
    );
  });
};

/* ============================================================
 * deviceId persistente por navegador/computador
 * ============================================================ */
const DEVICE_ID_KEY = "logvi_device_id";

function getOrCreateDeviceId(): string {
  try {
    let id = localStorage.getItem(DEVICE_ID_KEY);
    if (!id) {
      if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
        id = crypto.randomUUID();
      } else {
        id = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      }
      localStorage.setItem(DEVICE_ID_KEY, id);
    }
    return id;
  } catch {
    return "unknown-device";
  }
}

/* ============================================================
 * Hook simples para detectar mobile
 * ============================================================ */
function useIsMobile(breakpoint: number = 768): boolean {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const mq = window.matchMedia(`(max-width: ${breakpoint}px)`);
    const update = () => setIsMobile(mq.matches);

    update();

    if (mq.addEventListener) {
      mq.addEventListener("change", update);
      return () => mq.removeEventListener("change", update);
    } else {
      // @ts-ignore
      mq.addListener(update);
      // @ts-ignore
      return () => mq.removeListener(update);
    }
  }, [breakpoint]);

  return isMobile;
}

/* ============================================================
 * Estilos base (desktop)
 * ============================================================ */
const backgroundContainerDesktop: React.CSSProperties = {
  position: "fixed",
  top: 0,
  left: 0,
  width: "100vw",
  height: "100vh",
  background: "#1d2708",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 0,
};

const containerDesktop: React.CSSProperties = {
  width: 521,
  height: 590,
  background: "#fff",
  borderRadius: 12,
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  justifyContent: "flex-start",
  padding: "50px 34px",
  boxSizing: "border-box",
  zIndex: 1,
};

const inputContainerDesktop: React.CSSProperties = {
  width: "100%",
  maxWidth: 454,
  height: 54,
  border: "1px solid #000",
  borderRadius: 8,
  display: "flex",
  alignItems: "center",
  paddingLeft: 18,
  marginBottom: 28,
  boxSizing: "border-box",
  background: "#fff",
  position: "relative",
};

const inputBase: React.CSSProperties = {
  fontFamily: "Poppins, sans-serif",
  fontWeight: 500,
  fontSize: 16,
  color: "#8A8A8A",
  outline: "none",
  border: "none",
  background: "transparent",
  height: "100%",
  width: "100%",
  paddingRight: 36,
};

const eyeIconStyle: React.CSSProperties = {
  position: "absolute",
  right: 16,
  top: "50%",
  transform: "translateY(-50%)",
  cursor: "pointer",
  fontSize: 22,
  color: "#8A8A8A",
  background: "none",
  border: "none",
  display: "flex",
  alignItems: "center",
};

const linkBase: React.CSSProperties = {
  fontFamily: "Poppins, sans-serif",
  fontWeight: 500,
  fontSize: 16,
  color: "#9E201E",
  marginBottom: 20,
  cursor: "pointer",
};

const cadastroBase: React.CSSProperties = { ...linkBase, color: "#1d2708" };

const buttonBase: React.CSSProperties = {
  width: 454,
  height: 50,
  background: "#1d2708",
  borderRadius: 8,
  color: "#fff",
  fontFamily: "Poppins, sans-serif",
  fontWeight: 500,
  fontSize: 24,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  border: "none",
  cursor: "pointer",
  marginBottom: 0,
};

const feedbackStyle = (type: "error" | "success"): React.CSSProperties => ({
  marginTop: 10,
  marginBottom: 10,
  fontFamily: "Poppins, sans-serif",
  fontWeight: 500,
  fontSize: 14,
  color: type === "error" ? "#B00020" : "#0B8F3A",
});

// linha do "lembrar de mim"
const rememberRowBase: React.CSSProperties = {
  width: "100%",
  maxWidth: 454,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 12,
  marginBottom: 18,
};

const rememberText: React.CSSProperties = {
  fontFamily: "Poppins, sans-serif",
  fontWeight: 500,
  fontSize: 14,
  color: "#000",
};

// Checkbox custom
const cbWrap: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 10,
  cursor: "pointer",
};
const cbVisuallyHidden: React.CSSProperties = {
  position: "absolute",
  opacity: 0,
  width: 0,
  height: 0,
  pointerEvents: "none",
};
const cbBoxBase: React.CSSProperties = {
  width: 22,
  height: 22,
  borderRadius: 999,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  transition: "all .15s ease",
  boxShadow: "inset 0 0 0 2px #1d2708",
  background: "#fff",
};
const cbBoxChecked: React.CSSProperties = {
  background: "#1d2708",
  boxShadow: "inset 0 0 0 2px #1d2708",
};
const cbFocusRing: React.CSSProperties = {
  outline: "2px solid #1d2708",
  outlineOffset: 2,
};
const checkSvg = (
  <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true">
    <path
      d="M20 6L9 17l-5-5"
      fill="none"
      stroke="#fff"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// ⚠️ DICA: unifique com o REMEMBER_KEY do auth-context (lá está "eva_remember_me")
const REMEMBER_KEY = "eva_remember_me";

/* ============================================================
 * Formulário de login
 * ============================================================ */
const LoginForm: React.FC = () => {
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const { setTheme, setSystemTitle } = useTheme();
  const isMobile = useIsMobile();

  const [emailOrPhone, setEmailOrPhone] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState<boolean>(() => {
    try {
      const saved = localStorage.getItem(REMEMBER_KEY);
      return saved ? JSON.parse(saved) === true : false;
    } catch {
      return false;
    }
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState<{
    type: "error" | "success";
    msg: string;
  } | null>(null);
  const [cbFocused, setCbFocused] = useState(false);

  const email = useMemo(() => emailOrPhone.trim(), [emailOrPhone]);

  // estilos responsivos
  const backgroundContainer: React.CSSProperties = isMobile
    ? {
        minHeight: "100vh",
        width: "100%",
        background: "#1d2708",
        display: "flex",
        alignItems: "stretch",
        justifyContent: "center",
        padding: "16px",
        boxSizing: "border-box",
      }
    : backgroundContainerDesktop;

  const containerStyle: React.CSSProperties = isMobile
    ? {
        ...containerDesktop,
        width: "100%",
        maxWidth: 480,
        height: "auto",
        minHeight: 0,
        borderRadius: 12,
        padding: "32px 20px",
        alignItems: "flex-start",
      }
    : containerDesktop;

  const inputContainerStyle: React.CSSProperties = isMobile
    ? { ...inputContainerDesktop, maxWidth: "100%" }
    : inputContainerDesktop;

  const senhaContainerStyle: React.CSSProperties = {
    ...inputContainerStyle,
    marginBottom: 18,
  };

  const inputStyle: React.CSSProperties = {
    ...inputBase,
    fontSize: isMobile ? 14 : 16,
  };

  const linkStyle: React.CSSProperties = {
    ...linkBase,
    fontSize: isMobile ? 14 : 16,
  };

  const cadastroStyle: React.CSSProperties = {
    ...cadastroBase,
    fontSize: isMobile ? 14 : 16,
    marginTop: 4,
  };

  const buttonStyle: React.CSSProperties = isMobile
    ? { ...buttonBase, width: "100%", fontSize: 20, marginTop: 8 }
    : buttonBase;

  const rememberRow: React.CSSProperties = isMobile
    ? { ...rememberRowBase, maxWidth: "100%", flexDirection: "row" }
    : rememberRowBase;

  // persiste "lembrar de mim"
  useEffect(() => {
    try {
      localStorage.setItem(REMEMBER_KEY, JSON.stringify(remember));
    } catch {}
  }, [remember]);

  const validate = useCallback(() => {
    if (!email || !password) {
      setFeedback({
        type: "error",
        msg: "Preencha os campos para realizar o acesso.",
      });
      return false;
    }
    if (!email.includes("@")) {
      setFeedback({ type: "error", msg: "Credencial inválida." });
      return false;
    }
    return true;
  }, [email, password]);

  const handleSubmit = useCallback(async () => {
    if (loading) return;
    setFeedback(null);
    if (!validate()) return;

    try {
      setLoading(true);

      const deviceId = getOrCreateDeviceId();

      const payload: Record<string, any> = {
        email,
        senha: password,
        remember,
        deviceId,
      };

      // Não bloqueia o login por geolocalização
      getBrowserLocation()
        .then((loc) => {
          fetch("https://api.logvi-havox.com/auth/update-location", {
            method: "POST",
            credentials: "include",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
            },
            body: JSON.stringify({
              latitude: loc.latitude,
              longitude: loc.longitude,
              deviceId,
            }),
          }).catch(() => {});
        })
        .catch(() => {});

      const res = await fetch("https://api.logvi-havox.com/auth/login", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        const msg =
          data?.erro ||
          (res.status === 401 ? "Credenciais inválidas." : "Falha no login.");
        setFeedback({ type: "error", msg });
        return;
      }

      if (data?.theme) setTheme(data.theme);
      if (data?.systemTitle) setSystemTitle(data.systemTitle);
      if (data?.accessToken) {
        try {
          localStorage.setItem("access_token", data.accessToken);
        } catch {}
      }

      await refresh(); // ESSENCIAL
      setFeedback({ type: "success", msg: "Login bem-sucedido." });
    } catch {
      setFeedback({
        type: "error",
        msg: "Não foi possível conectar ao servidor.",
      });
    } finally {
      setLoading(false);
    }
  }, [
    loading,
    validate,
    email,
    password,
    remember,
    setTheme,
    setSystemTitle,
    refresh,
  ]);

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !loading) handleSubmit();
  };

  return (
    <div style={backgroundContainer}>
      <div style={containerStyle}>
        <div
          style={{
            fontFamily: "Poppins, sans-serif",
            fontWeight: 500,
            fontSize: isMobile ? 32 : 50,
            color: "#000",
            marginBottom: isMobile ? 4 : 5,
            textAlign: "left",
            width: "100%",
          }}
        >
          Entrar
        </div>

        <div
          style={{
            fontFamily: "Poppins, sans-serif",
            fontWeight: 500,
            fontSize: isMobile ? 13 : 14,
            color: "#000",
            marginBottom: isMobile ? 24 : 30,
            textAlign: "left",
            width: "100%",
          }}
        >
          Continue o seu acesso para o sistema Logvi da HAVOX
        </div>

        <div style={inputContainerStyle}>
          <input
            style={inputStyle}
            type="text"
            value={emailOrPhone}
            onChange={(e) => setEmailOrPhone(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="E-mail ou Telefone"
            autoComplete="username"
          />
        </div>

        <div style={senhaContainerStyle}>
          <input
            style={inputStyle}
            type={showPassword ? "text" : "password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Senha"
            autoComplete="current-password"
          />
          <span
            style={eyeIconStyle}
            onClick={() => setShowPassword((v) => !v)}
            tabIndex={0}
            aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
            role="button"
          >
            {showPassword ? <EyeClosed /> : <EyeOpen />}
          </span>
        </div>

        <div style={rememberRow}>
          <label htmlFor="rememberMe" style={cbWrap}>
            <input
              id="rememberMe"
              type="checkbox"
              checked={remember}
              onChange={(e) => setRemember(e.target.checked)}
              onFocus={() => setCbFocused(true)}
              onBlur={() => setCbFocused(false)}
              style={cbVisuallyHidden}
            />
            <span
              aria-hidden="true"
              style={{
                ...cbBoxBase,
                ...(remember ? cbBoxChecked : {}),
                ...(cbFocused ? cbFocusRing : {}),
              }}
            >
              {remember ? checkSvg : null}
            </span>
            <span style={rememberText}>Lembrar de mim</span>
          </label>

          <div style={{ ...linkStyle, marginBottom: 0 }}>
            <Link
              to="/redefinir-senha"
              style={{ textDecoration: "none", color: "inherit" }}
            >
              Esqueceu sua senha?
            </Link>
          </div>
        </div>

        {feedback && (
          <div style={feedbackStyle(feedback.type)} role="alert">
            {feedback.msg}
          </div>
        )}

        <div style={cadastroStyle}>
          <Link to="null" style={{ textDecoration: "none", color: "#1d2708" }}>
            Primeiro Acesso? Realize o seu Cadastro
          </Link>
        </div>

        <button
          style={{
            ...buttonStyle,
            opacity: loading ? 0.7 : 1,
            cursor: loading ? "not-allowed" : "pointer",
          }}
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? "Entrando..." : "Entrar"}
        </button>
      </div>
    </div>
  );
};

/* ============================================================
 * Página de login (checa sessão e decide)
 * ============================================================ */
const AcessLogin: React.FC = () => {
  const { sessao, loading } = useAuth();

  if (loading) return null;

  if (sessao) {
    const isSolicitante = Boolean(sessao.roles?.includes("solicitante"));

    return (
      <Navigate
        to={
          isSolicitante
            ? "/minhas-solicitacoes/laboratorio-cases"
            : "/suprimentos/produtos"
        }
        replace
      />
    );
  }

  return <LoginForm />;
};

export default AcessLogin;
